import get from "lodash/get";
import map from "lodash/map";
import partial from "lodash/partial";
import compact from "lodash/compact";
import max from "lodash/max";
import every from "lodash/every";
import sum from "lodash/sum";
import round from "lodash/round";
import merge from "lodash/merge";
import startsWith from "lodash/startsWith";
import includes from "lodash/includes";
import size from "lodash/size";
import getWilks from "../utils/getWilks";

const Competition = (app) => {
  app
    // Create or update an competition.
    .post(
      "/competition/save/:competition?",
      async ({ req, res, assert, flash, locals, api, load, momentTZ }) => {
        const { body, params } = req;
        await load("editCompetition", { competition: params.competition });
        const { editCompetition, activeUser } = locals;
        const _id = get(editCompetition, "_id");
        const isNew = !_id;
        const isFuture = body.tense === "future";
        body.user = activeUser._id;
        delete body.tense;

        assert(body.date, 400, "You must specify a day for the competition.");
        assert(body.name, 400, "You must specify a name for the competition.");
        assert(
          body.federation,
          400,
          "You must specify a federation for the competition."
        );
        assert(
          body.level,
          400,
          "You must specify a level for the competition."
        );
        assert(
          body.category,
          400,
          "You must specify a category for the competition."
        );
        assert(body.type, 400, "You must specify a type for the competition.");
        assert(
          body.weightClass,
          400,
          "You must specify a weight class for the competition."
        );

        if (!isFuture) {
          assert(
            body.weight,
            400,
            "You must specify a body weight for the competition."
          );
          assert(
            body.weight > 0,
            400,
            "The body weight must be greater than 0."
          );
          assert(
            body.attempts,
            400,
            "You must fill out your competition attempts."
          );
          assert(
            size(compact(body.attempts.bench)) >= 1,
            400,
            "You must fill out your competition bench press attempts."
          );

          if (includes(body.type, "Bench Only")) {
            delete body.attempts.squat;
            delete body.attempts.deadlift;
          } else {
            assert(
              size(compact(body.attempts.squat)) >= 1,
              400,
              "You must fill out your competition squat attempts."
            );
            assert(
              size(compact(body.attempts.deadlift)) >= 1,
              400,
              "You must fill out your competition deadlift attempts."
            );
          }

          // Automatically calculate total.
          const bestAttempts = _(body.attempts)
            .mapValues(partial(map, _, Number))
            .mapValues(compact)
            .map(max)
            .value();

          body.total = every(bestAttempts, (attempt) => attempt > 0)
            ? sum(bestAttempts)
            : 0;

          if (body.calculate) {
            body.wilks = round(
              getWilks(activeUser.gender, body.weight, body.total),
              1
            );
            merge(editCompetition, body);

            return res.refreshWith(
              {
                wilks: body.wilks,
                total: body.total,
              },
              { url: "back" }
            );
          }

          assert(
            body.wilks,
            400,
            "You must specify a competition wilks, or click calculate to do this automatically."
          );
        }

        if (isNew) {
          await api("competition", { method: "POST", body });
        } else {
          await api(`competition/${_id}`, { method: "PUT", body });
        }
        flash(
          "success",
          `Competition successfully ${isNew ? "added" : "updated"}.`
        );
        res.redirect(
          `/dashboard/competitions/?list=${isFuture ? "upcoming" : "previous"} `
        );
      }
    )
    // Cancel editing an competition and remove the edited item from the cache.
    .get("/competition/cancel", async ({ req, res, session }) => {
      const { query } = req;
      session.items.forEach(({ key }) => {
        if (startsWith(key, "editCompetition")) session.delete(key);
      });
      res.redirect(query.redirect);
    })
    // Remove an competition
    .get(
      "/competition/remove/:competition",
      async ({ req, res, assert, flash, locals, api, load, momentTZ }) => {
        if (
          process.browser &&
          !confirm("Are you sure you want to delete this competition?")
        ) {
          return res.backDontScroll();
        }

        const { body, params } = req;
        await load("editCompetition", { competition: params.competition });
        const { editCompetition } = locals;
        assert(editCompetition, 400, "Competition has already been removed.");

        const isFuture = momentTZ(editCompetition.date).isAfter(
          momentTZ().endOf("day")
        );
        await api(`competition/${editCompetition._id}`, { method: "DELETE" });
        flash("success", "Competition successfully removed.");
        res.redirect(
          `/dashboard/competitions/?list=${isFuture ? "upcoming" : "previous"}`
        );
      }
    );
};

export default Competition;
